/**
 * Blog Post: How to Use Permits Checker
 * Author: Compliance Helper (Jennifer Park)
 */

import type { BlogPost } from "../types";

export const howToUsePermitsPost: BlogPost = {
  slug: "how-to-use-permits",
  title: "Calgary Building Permits: A Step-by-Step Guide to Our Permit Checker",
  description:
    "Learn how to use Builder Buddy's Permit Checker to determine exactly what permits you need in Calgary, estimated costs, processing times, and required documents.",
  excerpt:
    "Our Permit Checker takes the guesswork out of Calgary's permit requirements. Here's how to use it effectively.",
  author: "compliance-helper",
  category: "permits",
  featuredImage: "/blog/permits-hero.jpg",
  tags: [
    "permits",
    "Calgary",
    "how-to",
    "building permit",
    "development permit",
  ],
  publishedAt: "2026-01-26",
  readingTime: 5,
  tableOfContents: [
    { id: "using-the-checker", title: "Using the Permit Checker", level: 2 },
    {
      id: "understanding-results",
      title: "Understanding Your Results",
      level: 2,
    },
    { id: "permit-types", title: "Common Permit Types", level: 2 },
    { id: "next-steps", title: "Next Steps After Checking", level: 2 },
  ],
  callToAction: {
    title: "Check your permits now",
    description: "Find out what permits you need for your project.",
    buttonText: "Open Permit Checker",
    buttonLink: "/permits",
  },
  content: `
Navigating Calgary's permit requirements can feel overwhelming. Building permits, development permits, electrical permits, plumbing permits - how do you know which ones you need? Our Permit Checker is designed to answer that question quickly and clearly.

## Using the Permit Checker

**Step 1: Select Your Building Type**

Choose between Residential and Commercial. This determines which permit categories apply to your project.

**Step 2: Select Your Project Type**

Pick the project that best describes your work:
- New Home Construction
- Addition/Extension
- Major Renovation
- Basement Development
- Secondary Suite
- Detached Garage/Accessory Building
- Deck/Patio
- Fence

**Step 3: Enter Project Details**

Provide basic specifications:
- Approximate square footage
- Number of stories
- Work scope (structural, electrical, plumbing, HVAC)

**Step 4: Review Your Results**

The checker will display:
- Required permits with estimated fees
- Processing times for each permit type
- Documents you'll need to submit
- Links to Calgary's permit portals

## Understanding Your Results

**Required vs. Conditional Permits**

Some permits are always required for certain project types. Others depend on the scope of work. We clearly mark which are mandatory and which are conditional.

**Fee Estimates**

Our estimates are based on Calgary's current fee schedules. Actual fees are calculated based on project value, so these are approximations to help you budget.

**Processing Times**

We provide typical timeframes, but these can vary based on:
- Current workload at the permit office
- Complexity of your project
- Completeness of your submission

**Required Documents Checklist**

For each permit, we list the typical documents required:
- Site plans
- Floor plans
- Building specifications
- Energy calculations
- Engineering reports (if applicable)

## Common Permit Types

**Building Permit (BP)**
Required for any construction work that affects the building structure or systems. This includes framing, insulation, drywall, windows, and doors.

**Development Permit (DP)**
Required when you're changing the use of property or requesting relaxations to zoning rules. Many residential projects don't need a DP if they comply with all zoning requirements.

**Electrical Permit**
Required for any electrical work including outlets, switches, panels, and fixtures. Must be done by a licensed electrician or qualified homeowner.

**Plumbing Permit**
Required when adding, moving, or modifying plumbing fixtures or drainage. Includes bathroom additions, kitchen changes, and water heater installations.

**HVAC/Gas Permit**
Required for heating, ventilation, and air conditioning work, plus any gas line installations or modifications.

**Trade Permits**
Electrical, plumbing, and HVAC permits are often called "trade permits" and may be obtained separately from the building permit.

## Next Steps After Checking

1. **Gather Your Documents** - Use the checklist to collect all required documentation before applying. Incomplete submissions are a common cause of delays.
2. **Hire Qualified Professionals** - Some permits require work to be done by licensed professionals. Electrical and gas work have strict licensing requirements in Alberta.
3. **Submit Your Applications** - Calgary offers online submission through their Development and Building Approvals portal. We provide direct links in the permit checker results.
4. **Schedule Inspections** - Once you have permits, you'll need inspections at various stages of construction. Plan for this in your project timeline.
5. **Keep Records** - Maintain copies of all permits and inspection reports. You'll need these for final occupancy and if you ever sell the property.

**Important Reminder**

While our Permit Checker provides comprehensive guidance, permit requirements can change. We recommend confirming requirements with the City of Calgary for your specific project. Our tool is designed to help you prepare and ask the right questions, not replace official consultation.
`,
};
